// Mock implementation of the BACKEND.md contract.
// Used when VITE_USE_MOCK_API=true (default in dev) so the new UX can be
// developed offline without a Salesforce-connected backend. Each function
// mirrors a REST endpoint described in BACKEND.md §REST API.
//
// Do NOT add real network logic here — that lives in api/real.ts.

import {
  PACKS,
  PHASES,
  PLAYBOOKS,
  PROFILES_LIST,
  RECENT_RUNS,
  STORIES,
  TEMPLATES,
} from "@/lib/data";
import type {
  JobPace,
  Profile,
  Run,
  RunConfig,
  RunPhase,
  Story,
  TableauPatOneOff,
  TableauProfile,
  Template,
} from "@/lib/types";

let tableauProfiles: TableauProfile[] = [];

const sleep = (ms: number): Promise<void> =>
  new Promise((resolve) => setTimeout(resolve, ms));

let runs: Run[] = [...RECENT_RUNS];

function newRunId(): string {
  return `r${Math.floor(Math.random() * 1e9).toString(36)}`;
}

export const mockApi = {
  async listProfiles(): Promise<Profile[]> {
    await sleep(60);
    return PROFILES_LIST;
  },
  async getProfile(id: string): Promise<Profile | undefined> {
    await sleep(40);
    return PROFILES_LIST.find((p) => p.id === id);
  },
  async listPacks(): Promise<Profile[]> {
    await sleep(40);
    return PACKS;
  },
  async listStories(): Promise<Story[]> {
    await sleep(40);
    return STORIES;
  },
  async listTemplates(): Promise<Template[]> {
    await sleep(40);
    return TEMPLATES;
  },
  async listPlaybooks(): Promise<typeof PLAYBOOKS> {
    await sleep(40);
    return PLAYBOOKS;
  },
  async listRuns(): Promise<Run[]> {
    await sleep(80);
    return runs;
  },
  async getRun(id: string): Promise<Run | undefined> {
    await sleep(40);
    return runs.find((r) => r.id === id);
  },
  async getRunWithRaw(id: string) {
    await sleep(40);
    const run = runs.find((r) => r.id === id);
    return { run, raw: null };
  },
  async createRun(config: RunConfig): Promise<Run> {
    await sleep(120);
    const profile = PACKS.find((p) => p.id === config.profileId);
    const layers = (Object.entries(config.pipeline)
      .filter(([, v]) => v)
      .map(([k]) => k) as Run["layers"]);
    const run: Run = {
      id: newRunId(),
      profile: profile?.name,
      profileId: config.profileId,
      volume: config.volume,
      org: "orgfarm-mock",
      status: "queued",
      startedAt: new Date().toISOString(),
      duration: "—",
      storiesCount: config.stories.length,
      stories: config.stories,
      owner: "you",
      layers,
      cloud:
        config.salesCloud && config.serviceCloud
          ? "Sales + Service"
          : config.salesCloud
            ? "Sales only"
            : "Service only",
      config,
      phases: PHASES.map(
        (p): RunPhase => ({
          id: p.id,
          label: p.label,
          status: "queued",
          pct: 0,
          steps: p.steps.map((label) => ({ label, done: false })),
        }),
      ),
    };
    runs = [run, ...runs];
    return run;
  },
  /** Stream of phase updates for a run. Returns an unsubscribe function. */
  subscribeToRun(
    runId: string,
    onPhase: (phase: RunPhase) => void,
    onComplete: () => void,
  ): () => void {
    const run = runs.find((r) => r.id === runId);
    if (!run || !run.phases) {
      onComplete();
      return () => undefined;
    }
    let cancelled = false;
    let phaseIdx = 0;
    let pct = 0;

    const tick = (): void => {
      if (cancelled) return;
      if (phaseIdx >= run.phases!.length) {
        run.status = "complete";
        onComplete();
        return;
      }
      const phase = run.phases![phaseIdx];
      phase.status = "running";
      pct += 18 + Math.random() * 22;
      if (pct >= 100) {
        phase.pct = 100;
        phase.status = "done";
        phase.steps.forEach((s) => (s.done = true));
        onPhase({ ...phase });
        phaseIdx += 1;
        pct = 0;
      } else {
        phase.pct = Math.round(pct);
        const stepN = Math.floor((pct / 100) * phase.steps.length);
        phase.steps.forEach((s, i) => (s.done = i < stepN));
        onPhase({ ...phase });
      }
      setTimeout(tick, 700);
    };

    setTimeout(tick, 300);
    return () => {
      cancelled = true;
    };
  },
  async cancelRun(id: string): Promise<void> {
    runs = runs.map((r) => (r.id === id ? { ...r, status: "cancelled" } : r));
  },
  async retryStep(_id: string, _step: string): Promise<void> {
    // no-op in mock
  },
  async preflight(): Promise<{ ok: boolean; checks?: { name: string; passed: boolean }[]; warnings?: string[] } | null> {
    return { ok: true, checks: [{ name: "Mock preflight", passed: true }] };
  },
  async getJobPace(_runId: string): Promise<JobPace> {
    // Mock returns the heuristic tier so dev UI exercises the
    // "estimate · no history yet" path that real fresh-config runs hit.
    return {
      expected_total_sec: 45 * 60,
      expected_remaining_sec: 45 * 60,
      elapsed_sec: 0,
      current_step: null,
      current_step_baseline_sec: null,
      current_step_elapsed_sec: 0,
      pace_status: "heuristic",
      baseline_runs_count: 0,
      baseline_window_days: 30,
      min_baseline_runs: 3,
      is_heuristic: true,
    };
  },
  async getOrgInventory() {
    return {
      org_connection_id: "mock-org",
      fetched_at: new Date().toISOString(),
      cache_hit: false,
      counts: { Account: 0, Opportunity: 0, Contact: 0, Case: 0, Lead: 0 },
      detected_pack_id: null,
      detected_pack_confidence: 0,
    };
  },

  // ── Tableau Cloud PAT profiles (PR 1 Week 3) ──
  async listTableauProfiles(): Promise<TableauProfile[]> {
    await sleep(40);
    return [...tableauProfiles];
  },
  async createTableauProfile(payload: {
    label: string;
    serverUrl: string;
    siteName: string;
    patName: string;
    patSecret: string;
    isDefault: boolean;
  }): Promise<TableauProfile> {
    await sleep(80);
    const profile: TableauProfile = {
      id: `tab-${Math.floor(Math.random() * 1e9).toString(36)}`,
      label: payload.label,
      serverUrl: payload.serverUrl,
      siteName: payload.siteName,
      patName: payload.patName,
      isDefault: payload.isDefault,
      createdAt: new Date().toISOString(),
      lastUsedAt: null,
      lastValidatedAt: new Date().toISOString(),
    };
    if (payload.isDefault) {
      tableauProfiles = tableauProfiles.map((p) => ({ ...p, isDefault: false }));
    }
    tableauProfiles = [profile, ...tableauProfiles];
    return profile;
  },
  async deleteTableauProfile(id: string): Promise<void> {
    await sleep(40);
    tableauProfiles = tableauProfiles.filter((p) => p.id !== id);
  },
  async testTableauPat(_payload: TableauPatOneOff): Promise<{
    ok: boolean;
    detail: string | null;
    user_name: string | null;
  }> {
    await sleep(120);
    return { ok: true, detail: "Mock sign-in succeeded", user_name: "Mock User" };
  },

  // PR 6 — mock chat that always proposes a demo-reset bundle.
  async tabExtensionsChat(payload: {
    user_message: string;
    history?: { role: string; content: string }[];
  }) {
    await sleep(120);
    return {
      proposed_bundle: {
        slug: "demo-reset",
        template: "demo_reset_panel",
        title_override: "Demo Reset",
        config: { show_polish_button: true, show_shift_dates_button: true },
        ai_brief: payload.user_message,
        ai_iterations: (payload.history?.length ?? 0) / 2 + 1,
      },
      preview_html: "<!doctype html><body>Mock preview — buttons disabled.</body>",
      rationale: "Mock proposal for offline development. Wired to demo_reset_panel.",
      iterations: 1,
      errors: [] as string[],
    };
  },
  async tabExtensionsPreview() {
    await sleep(40);
    return {
      preview_html: "<!doctype html><body>Mock preview.</body>",
      errors: [] as string[],
    };
  },

  // PR 5 — mock polisher that always returns a small green payload.
  async polishOrg(_useCache = true) {
    await sleep(80);
    return {
      severity: "green" as const,
      message: "Org is demo-ready ✓ (mock)",
      checks: [
        {
          name: "crm_data.account",
          severity: "green" as const,
          title: "Account loaded — 50 rows",
          detail: "Freshest record at 2026-06-04T12:00",
        },
      ],
      ran_at: new Date().toISOString(),
      cached: false,
      cache_ttl_seconds: 300,
    };
  },
};

export type MockApi = typeof mockApi;
